import useEventsData, { IEvent } from "@/src/UseData/useEventsData";
import { IWPLink } from "@/src/types";
import { MainSection } from "@/styles/global";
import React, { useRef, useCallback } from "react";
import S from "./LatestEvents.styled";
import FilledArrow from "@/assets/filled-arrow.svg";
import {
  Scrollbar,
  FreeMode,
  Mousewheel,
  Navigation,
  Pagination,
} from "swiper/modules";
import SwiperCore from "swiper";
import { Swiper, SwiperSlide } from "swiper/react";
import "swiper/swiper-bundle.css";
import "swiper/css";
import "swiper/css/scrollbar";
import Button from "@/components/global-components/Button";
import EventCard from "@/components/global-components/EventCard";
import { parseDate } from "@/src/helpers";

SwiperCore.use([Navigation, Pagination, Scrollbar]);

export interface LatestEventsProps {
  title: string;
  link: IWPLink;
}

const LatestEvents: React.FC<LatestEventsProps> = ({ title, link }) => {
  const removeOldEvents = (events: IEvent[]) => {
    return events
      .sort((a, b) => {
        const dateA = parseDate(a.eventsInfo.datepicker).getTime();
        const dateB = parseDate(b.eventsInfo.datepicker).getTime();
        return dateA - dateB;
      })
      .filter((event: IEvent) => {
        const eventEndDate = event.eventsInfo.datepickerEnd
          ? parseDate(event.eventsInfo.datepickerEnd)
          : parseDate(event.eventsInfo.datepicker);

        const today = new Date();

        const eventEndDateOnly = new Date(
          eventEndDate.getFullYear(),
          eventEndDate.getMonth(),
          eventEndDate.getDate()
        );
        const todayOnly = new Date(
          today.getFullYear(),
          today.getMonth(),
          today.getDate()
        );

        return eventEndDateOnly >= todayOnly;
      });
  };

  const eventsData = removeOldEvents(useEventsData()).slice(0, 6);

  const sliderRef = useRef<React.ElementRef<typeof Swiper>>(null);
  const [isBeginning, setIsBeginning] = React.useState(true);
  const [isEnd, setIsEnd] = React.useState(false);

  React.useEffect(() => {
    if (!sliderRef.current) return;
    sliderRef.current.swiper.on("slideChange", () => {
      if (sliderRef.current) {
        setIsBeginning(sliderRef.current.swiper.isBeginning);
        setIsEnd(sliderRef.current.swiper.isEnd);
      }
    });
  }, [sliderRef]);

  const handlePrev = useCallback(() => {
    if (!sliderRef.current) return;
    sliderRef.current.swiper.slidePrev();
  }, []);

  const handleNext = useCallback(() => {
    if (!sliderRef.current) return;
    sliderRef.current.swiper.slideNext();
  }, []);

  const renderCards = eventsData?.map(({ eventsInfo, uri, title }, index) => {
    return (
      <SwiperSlide key={index} className="swiperSlide">
        <EventCard
          eventsInfo={eventsInfo}
          uri={uri}
          title={title}
          hasThumbnail="image"
        />
      </SwiperSlide>
    );
  });

  return (
    <MainSection $bgColor="#EAEAEF">
      <S.Inner>
        <S.TitleWrapper>
          {title && <S.Title>{title}</S.Title>}
          <Button link={link} type="textLink" arrow />
        </S.TitleWrapper>
        <S.CardsWrapper>
          <Swiper
            modules={[Scrollbar, FreeMode, Mousewheel]}
            ref={sliderRef}
            className="mySwiper"
            spaceBetween={16}
            breakpoints={{
              350: {
                slidesPerView: 1.1,
              },
              530: {
                slidesPerView: 1.5,
              },
              800: {
                slidesPerView: 2,
              },
              1200: {
                slidesPerView: 2.4,
              },
            }}
            mousewheel={{
              forceToAxis: true,
            }}
          >
            {renderCards}
          </Swiper>
        </S.CardsWrapper>
        <S.NavigationWrapper>
          <S.Navigation onClick={handlePrev} disabled={isBeginning}>
            <FilledArrow className="firstArrow" />
          </S.Navigation>
          <S.Navigation onClick={handleNext} disabled={isEnd}>
            <FilledArrow />
          </S.Navigation>
        </S.NavigationWrapper>
      </S.Inner>
    </MainSection>
  );
};

export default LatestEvents;
